import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "control" }
const _hoisted_2 = { class: "field mb-0" }
const _hoisted_3 = { class: "control has-icons-left" }
const _hoisted_4 = ["onKeyup"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 0,
  class: "navbar-search-dropdown navbar-dropdown"
}
const _hoisted_7 = {
  key: 0,
  class: "navbar-item"
}
const _hoisted_8 = {
  key: 0,
  class: "navbar-divider"
}
const _hoisted_9 = { class: "navbar-item navbar-dropdown-header" }
const _hoisted_10 = ["href"]
const _hoisted_11 = { class: "media" }
const _hoisted_12 = {
  key: 0,
  class: "media-left image is-48x48"
}
const _hoisted_13 = ["src"]
const _hoisted_14 = { class: "media-content" }
const _hoisted_15 = {
  key: 0,
  class: "has-text-weight-semibold"
}
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { key: 2 }

import { ref, computed, onMounted, watch } from "vue";
import Turbolinks from "turbolinks";
import { debounce } from "lodash-es";

interface Props {
  searchIcon: string;
  searchParam?: string;
}

const searchUrl = "/search.json";

export default /*@__PURE__*/_defineComponent({
  __name: 'search',
  props: {
    searchIcon: {},
    searchParam: { default: "" }
  },
  setup(__props: any) {

const props = __props;

const query = ref(props.searchParam);
const searchResults = ref<Record<string, any>>({});
const plurals = {
  Game: "games",
  Series: "series",
  Company: "companies",
  Platform: "platforms",
  Engine: "engines",
  Genre: "genres",
  User: "users"
};
const activeSearchResult = ref(-1);
const currentPage = ref(1);
const moreAlreadyLoaded = ref(false);

onMounted(() => {
  if (props.searchParam.length > 0) {
    onSearch();
  }
});

// Debounce the search for 400ms before showing results, to prevent
// searching from sending a ton of requests.
const onSearch = debounce(() => {
  if (query.value.length > 1) {
    fetch(`${searchUrl}?query=${query.value}`)
      .then((response) => {
        return response.json();
      })
      .then((results) => {
        searchResults.value = results;
        activeSearchResult.value = -1;
      });
  }
}, 400);

function onUpArrow() {
  if (activeSearchResult.value >= 0) {
    activeSearchResult.value = activeSearchResult.value - 1;
    scrollToActiveItem();
  }
}

function onDownArrow() {
  if (activeSearchResult.value < flattenedSearchResults.value.length - 1) {
    activeSearchResult.value = activeSearchResult.value + 1;
    scrollToActiveItem();
  }
}

// On enter, have turbolinks navigate to the active item's linked page.
function onEnter() {
  const activeItem: HTMLLinkElement | null = document.querySelector(
    ".navbar-search-dropdown .navbar-item.is-active"
  );
  if (activeItem !== null) {
    Turbolinks.visit(activeItem.href);
  }
}

function scrollToActiveItem() {
  // Select the current active item and the searchDropdown.
  const activeItem: HTMLElement | null = document.querySelector(
    ".navbar-search-dropdown .navbar-item.is-active"
  );
  const searchDropdown: HTMLElement | null = document.querySelector(".navbar-search-dropdown");
  // If the activeItem exists, scroll to it as the user moves through the dropdown options.
  if (activeItem !== null && searchDropdown !== null) {
    searchDropdown.scrollTop = activeItem.offsetTop - searchDropdown.offsetTop;
  }
}

// Load more games if the user selects "More...".
function onMoreGames() {
  currentPage.value += 1;
  fetch(`${searchUrl}?query=${query.value}&page=${currentPage.value}&only_games=true`)
    .then((response) => {
      return response.json();
    })
    .then((results) => {
      searchResults.value["Game"] = searchResults.value["Game"].concat(results["Game"]);
      activeSearchResult.value = -1;
      // If there are a multiple of 15 results and no new games are
      // added, the component will still show a "More..." button. This
      // sets 'moreAlreadyLoaded' to true to make sure the 'More...'
      // button is hidden and handle this edge case.
      if (results["Game"].length === 0) {
        moreAlreadyLoaded.value = true;
      }
    });
}

// Determine if the dropdown is active so we can display it when it is.
const dropdownActive = computed(() => {
  return query.value.length > 1;
});

// Toggle is-active on the mount target element, since Bulma requires
// it on the .has-dropdown element for the dropdown to display.
watch(dropdownActive, (active) => {
  const mountTarget = document.querySelector('[data-vue-component="search"]');
  if (mountTarget) {
    mountTarget.classList.toggle("is-active", active);
  }
});

const hasSearchResults = computed(() => {
  return Object.values(searchResults.value).flat().length != 0;
});

// Do a stupid hack to capitalize the first letter of each plural value,
// e.g. "Games", "Companies", etc.
const capitalizedPlurals = computed(() => {
  const capitalizedPluralEntries = Object.entries(plurals).map((type: Array<any>) => {
    type[1] = type[1].charAt(0).toUpperCase() + type[1].slice(1);
    return type;
  });

  return Object.fromEntries(capitalizedPluralEntries);
});

const betterSearchResults = computed(() => {
  const betterResults = { ...searchResults.value };
  Object.keys(betterResults).forEach((key) => {
    if (betterResults[key].length == 0) {
      delete betterResults[key];
      return true;
    }
    betterResults[key].map((result: any) => {
      // Use the slug in the URL if it's a user.
      const url_key = result.searchable_type === "User" ? result.slug : result.searchable_id;
      result.url = `/${plurals[result.searchable_type as keyof typeof plurals]}/${url_key}`;
      return result;
    });
  });

  return betterResults;
});

const flattenedSearchResults = computed(() => {
  return Object.values(searchResults.value).flat();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("p", _hoisted_3, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((query).value = $event)),
            onInput: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onSearch) && _unref(onSearch)(...args))),
            onKeyup: [
              _withKeys(_withModifiers(onUpArrow, ["prevent"]), ["up"]),
              _withKeys(_withModifiers(onDownArrow, ["prevent"]), ["down"]),
              _withKeys(_withModifiers(onEnter, ["prevent"]), ["enter"])
            ],
            class: "input navbar-search-input",
            type: "search",
            placeholder: "Search"
          }, null, 40, _hoisted_4), [
            [_vModelText, query.value]
          ]),
          _createElementVNode("span", {
            class: "icon is-small is-left",
            innerHTML: __props.searchIcon
          }, null, 8, _hoisted_5)
        ])
      ])
    ]),
    (dropdownActive.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (!hasSearchResults.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, "No results."))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.keys(betterSearchResults.value), (type, index) => {
            return (_openBlock(), _createElementBlock("div", { key: type }, [
              (index > 0)
                ? (_openBlock(), _createElementBlock("hr", _hoisted_8))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_9, _toDisplayString(capitalizedPlurals.value[type]), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(betterSearchResults.value[type], (result) => {
                return (_openBlock(), _createElementBlock("a", {
                  key: result.id,
                  href: result.url,
                  class: _normalizeClass(["navbar-item", {
            'is-active':
              activeSearchResult.value !== -1 &&
              flattenedSearchResults.value[activeSearchResult.value].searchable_id === result.searchable_id
          }])
                }, [
                  _createElementVNode("div", _hoisted_11, [
                    (type === 'Game' || type === 'User')
                      ? (_openBlock(), _createElementBlock("figure", _hoisted_12, [
                          _createElementVNode("img", {
                            src: result.image_url,
                            width: "48px",
                            height: "48px",
                            class: "game-cover"
                          }, null, 8, _hoisted_13)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_14, [
                      (type === 'Game')
                        ? (_openBlock(), _createElementBlock("p", _hoisted_15, _toDisplayString(result.content), 1))
                        : (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(result.content), 1)),
                      (type === 'Game')
                        ? (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString([
                    result.release_date === null ? "" : result.release_date.slice(0, 4),
                    result.developer === null ? "" : result.developer
                  ]
                    .filter((x) => x !== "")
                    .join(" · ")), 1))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ], 10, _hoisted_10))
              }), 128)),
              (
            type === 'Game' && betterSearchResults.value[type].length % 15 === 0 && !moreAlreadyLoaded.value
          )
                ? (_openBlock(), _createElementBlock("a", {
                    key: 1,
                    class: "navbar-item",
                    onClick: onMoreGames
                  }, [...(_cache[2] || (_cache[2] = [
                    _createElementVNode("div", { class: "media" }, [
                      _createElementVNode("div", { class: "media-content" }, [
                        _createElementVNode("p", null, "More...")
                      ])
                    ], -1)
                  ]))]))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})